import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import { errMsg, formatDate, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { HelpLang, useHelpLang } from "@/components/help/HelpLang";
import { NewRequestDialog } from "./NewRequestDialog";
import { Attachments, useCategories } from "./SupportAttachments";

const TONE = { open: "bg-sky-50 text-sky-700", in_progress: "bg-amber-50 text-amber-800", waiting_client: "bg-violet-50 text-violet-700", done: "bg-emerald-50 text-emerald-700", closed: "bg-slate-100 text-slate-500" };

export function StatusPill({ r, staff }) {
  const { t } = useTranslation("help");
  return <span className={`rounded px-1.5 py-0.5 text-[11px] ${TONE[r.status]}`} data-testid={`support-state-${r.id}`}>{t(staff && r.status === "waiting_client" ? "req.waiting_client_staff" : `req.status.${r.status}`)}</span>;
}

// One request thread; used by the client area (tid = own tenant) and by the MUSORA staff inbox (tid = the request's tenant).
export function Thread({ tid, r, d, onChange }) {
  const api = useApi();
  const { t, lang } = useHelpLang();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); setText(""); onChange(); } catch (e) { toast.error(`${t("dialog.failed")}: ${errMsg(e)}`); } setBusy(false); };
  const canWrite = d.can_create && (r.status !== "closed" || d.is_staff);
  return (
    <div className="space-y-2 border-t border-slate-100 pt-2" data-testid={`support-thread-${r.id}`}>
      {r.messages.map((m) => <div key={m.id} className={`rounded-md px-3 py-2 text-sm ${m.by === "support" ? "bg-sky-50 text-sky-900" : "bg-slate-50 text-slate-700"}`}>
        <p className="mb-0.5 text-[11px] text-slate-500">{m.by === "support" ? t("req.staff_name") : m.email} · {formatDate(m.at, true, lang)}</p><p className="whitespace-pre-wrap">{m.text}</p></div>)}
      <Attachments tid={tid} r={r} max={d.limits?.files || 3} canAdd={canWrite} onChange={onChange} />
      {canWrite && <div className="space-y-2">
        <Textarea rows={2} value={text} maxLength={5000} onChange={(e) => setText(e.target.value)} placeholder={t("req.reply_ph")} data-testid={`support-reply-${r.id}`} />
        <Button size="sm" className="h-11" disabled={busy || !text.trim()} onClick={() => run(() => api.post(`/t/${tid}/support-requests/${r.id}/messages`, { text }), t("req.msg_sent"))} data-testid={`support-reply-send-${r.id}`}>{t("req.send_reply")}</Button></div>}
      {d.is_staff && <p className="pt-1 text-[11px] font-medium text-slate-600">{t("req.status_staff")}</p>}
      {d.is_staff && <Select value={r.status} onValueChange={(v) => run(() => api.patch(`/t/${tid}/support-requests/${r.id}`, { status: v }), t("req.status_updated"))}>
        <SelectTrigger className="h-11 w-64" data-testid={`support-status-${r.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>{Object.keys(d.statuses).map((k) => <SelectItem key={k} value={k}>{t(k === "waiting_client" ? "req.waiting_client_staff" : `req.status.${k}`)}</SelectItem>)}</SelectContent></Select>}
    </div>);
}

export function RequestRow({ r, open, setOpen, cats, extra, staff, children }) {
  const { t, lang } = useHelpLang();
  return (
    <section className="space-y-1 rounded-lg border border-slate-200 bg-white p-4" data-testid={`support-request-${r.id}`}>
      <button type="button" className="flex min-h-[40px] w-full flex-wrap items-center gap-2 text-left" onClick={() => setOpen(open === r.id ? null : r.id)} aria-expanded={open === r.id} data-testid={`support-open-${r.id}`}>
        <span className="text-sm font-medium text-slate-800">{r.title}</span><StatusPill r={r} staff={staff} />
        <span className="text-xs text-slate-500">{extra}{t(`req.type.${r.type}`)} · {cats[r.category] || t("cat_other")}{r.site_name ? ` · ${r.site_name}` : ""} · {formatDate(r.updated_at, true, lang)}</span></button>
      {open === r.id && children}
    </section>);
}

// «Le tue richieste» inside the Centro assistenza.
export default function SupportRequests({ tid, preset, showLang = true }) {
  const api = useApi();
  const { t, lang } = useHelpLang();
  const cats = useCategories(lang);
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [create, setCreate] = useState(!!preset);
  const load = useCallback(() => api.get(`/t/${tid}/support-requests`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [api, tid]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="animate-spin text-slate-400" />;
  return (
    <div className="space-y-3" lang={lang} data-testid="support-requests">
      {showLang && <div className="flex justify-end"><HelpLang lang={lang} /></div>}
      <p className="text-sm text-slate-600">{t("req.intro")}</p>
      {d.can_create ? <Button className="h-11" onClick={() => setCreate(true)} data-testid="support-new"><Plus size={15} className="mr-1.5" />{d.is_staff ? t("req.new_staff") : t("req.new")}</Button>
        : <p className="text-xs text-slate-500" data-testid="support-readonly">{t("req.readonly")}</p>}
      {d.requests.length === 0 && <p className="text-sm text-slate-500" data-testid="support-empty">{t("req.empty")}</p>}
      {d.requests.map((r) => <RequestRow key={r.id} r={r} open={open} setOpen={setOpen} cats={cats} staff={d.is_staff}><Thread tid={tid} r={r} d={d} onChange={load} /></RequestRow>)}
      <NewRequestDialog open={create} onOpenChange={setCreate} tid={tid} types={d.types} categories={cats} maxFiles={d.limits?.files} preset={preset} onCreated={() => { setCreate(false); load(); }} />
    </div>);
}
